import { useMemo } from "react";
import {
  formatPhoneHours,
  getLocalTimeZoneCode,
  getPhoneServiceHours,
  isWithinServiceHours,
  useChatInternalContext,
} from "@b2bportal/core-chat";
import { ChatAvailability } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";

export function useContactSupportDetails() {
  const { supportId } = useChatInternalContext();
  const { brand, locale, t } = useI18nContext();
  const {
    contactSupport: {
      chatAvailability,
      displaySupportNumber,
      displayIntlSupportNumber,
      hideFaqLinks = false,
      intlSupportNumber,
      phoneAvailability,
      supportNumber,
      hideSupportIdInContactSupport = false,
    },
    urls: { helpCenterBaseUrl },
  } = brand;
  const showChatOption = chatAvailability !== ChatAvailability.None;
  const [startUtc, endUtc] = getPhoneServiceHours(phoneAvailability);
  const enableCallOption = isWithinServiceHours(startUtc, endUtc);

  /**
   * Gets the i18n context for the call option subtitle copy
   */
  const callOptionSubtitle = useMemo(() => {
    if (enableCallOption) {
      if (startUtc === 0 && endUtc === 24) {
        return t("chat.contactSupport.callOptionHOOP");
      }

      return t("chat.contactSupport.callOptionHOOP", {
        context: "limited",
        startHour: formatPhoneHours(startUtc, locale),
        endHour: formatPhoneHours(endUtc, locale),
        timezone: getLocalTimeZoneCode(),
      });
    }

    return t("chat.contactSupport.callOptionHOOP", {
      context: "unavailable",
      startHour: formatPhoneHours(startUtc, locale),
      endHour: formatPhoneHours(endUtc, locale),
      timezone: getLocalTimeZoneCode(),
    });
  }, [enableCallOption, endUtc, locale, startUtc, t]);

  return {
    context: {
      additionalInfoI18nKey: "chat.additionalInfo",
      enableCallOption,
      helpCenterBaseUrl,
      hideFaqLinks,
      hideSupportIdInContactSupport,
      intlSupportNumber,
      showChatOption,
      supportId,
      supportNumber,
    },
    handlers: {},
    text: {
      callOptionSubtitle,
      callOptionIntlSubtitle: t(
        "chat.contactSupport.callOptionPhoneNumberIntl",
        {
          phoneNumber: displayIntlSupportNumber,
        }
      ),
      callOptionIntlTitle: t("chat.contactSupport.callOptionTitleIntl"),
      callOptionLocalSubtitle: t(
        "chat.contactSupport.callOptionPhoneNumberLocal",
        {
          phoneNumber: displaySupportNumber,
        }
      ),
      callOptionLocalTitle: t("chat.contactSupport.callOptionTitle"),
      chatOptionSubtitle: t("chat.contactSupport.chatOptionSubtitle"),
      chatOptionTitle: t("chat.contactSupport.chatOptionTitle"),
      contactSupportSubtitle: t("chat.contactSupport.subtitle"),
      contactSupportTitle: t("chat.contactSupport.title"),
      supportIdLabel: t("chat.contactSupport.supportIdLabel"),
    },
  };
}
